// The schedule of a regular analysis — PDP analysis per page, Consumer
// Voice and AI Presence per product: the interval, then the day it runs
// on (a weekday for weekly / every 2 weeks, a date for monthly; daily has
// none). Intervals faster than the brand's plan allows are shown but
// disabled. One component so the five places that set a schedule read the
// same (lib/analysisSchedule on the API decides when a row is due).
import React from "react";
import { MenuItem, TextField } from "@mui/material";
import {
    DAYS_OF_MONTH,
    INTERVAL_LABELS,
    PDP_SCORE_INTERVALS,
    WEEKDAY_LABELS,
    intervalLabel,
    ordinal,
} from "../../utils/plans";

export interface AnalysisSchedule {
    interval: string;
    weekday: number | null;
    dayOfMonth: number | null;
}

interface Props {
    label: string;
    value: AnalysisSchedule;
    onChange: (next: AnalysisSchedule) => void;
    // The fastest the brand's plan allows; faster options are disabled.
    maxInterval?: string | null;
    // Offer "" as "Default" (the Add page form: the API picks weekly or the cap).
    allowDefault?: boolean;
    helperText?: string;
    disabled?: boolean;
    size?: "small" | "medium";
    className?: string;
}

const needsWeekday = (interval: string) => interval === "weekly" || interval === "biweekly";
const needsDayOfMonth = (interval: string) => interval === "monthly";

export function AnalysisScheduleFields({
    label,
    value,
    onChange,
    maxInterval = null,
    allowDefault = false,
    helperText,
    disabled = false,
    size = "small",
    className,
}: Props) {
    const capIdx = maxInterval ? PDP_SCORE_INTERVALS.indexOf(maxInterval as any) : -1;
    const help =
        helperText ||
        (maxInterval ? `The brand's plan allows ${intervalLabel(maxInterval).toLowerCase()} at most.` : undefined);
    return (
        <div className={className} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            <TextField
                select
                size={size}
                label={label}
                value={value.interval}
                onChange={(e) => onChange({ ...value, interval: e.target.value })}
                disabled={disabled}
                helperText={help}
                sx={{ flex: "1 1 180px", minWidth: 180 }}
            >
                {allowDefault ? <MenuItem value="">Default</MenuItem> : null}
                {PDP_SCORE_INTERVALS.map((i, idx) => (
                    <MenuItem key={i} value={i} disabled={capIdx > 0 && idx < capIdx}>
                        {INTERVAL_LABELS[i]}
                        {capIdx > 0 && idx < capIdx ? " (above plan)" : ""}
                    </MenuItem>
                ))}
            </TextField>
            {needsWeekday(value.interval) ? (
                <TextField
                    select
                    size={size}
                    label="On"
                    value={value.weekday === null || value.weekday === undefined ? "" : String(value.weekday)}
                    onChange={(e) => onChange({ ...value, weekday: e.target.value === "" ? null : Number(e.target.value) })}
                    disabled={disabled}
                    helperText={value.interval === "biweekly" ? "Every other week on this day." : "Every week on this day."}
                    sx={{ flex: "1 1 150px", minWidth: 150 }}
                >
                    {WEEKDAY_LABELS.map((name, idx) => (
                        <MenuItem key={name} value={String(idx)}>
                            {name}
                        </MenuItem>
                    ))}
                </TextField>
            ) : null}
            {needsDayOfMonth(value.interval) ? (
                <TextField
                    select
                    size={size}
                    label="On the"
                    value={value.dayOfMonth === null || value.dayOfMonth === undefined ? "" : String(value.dayOfMonth)}
                    onChange={(e) => onChange({ ...value, dayOfMonth: e.target.value === "" ? null : Number(e.target.value) })}
                    disabled={disabled}
                    helperText="Each month on this date (the last day in shorter months)."
                    sx={{ flex: "1 1 130px", minWidth: 130 }}
                >
                    {DAYS_OF_MONTH.map((d) => (
                        <MenuItem key={d} value={String(d)}>
                            {ordinal(d)}
                        </MenuItem>
                    ))}
                </TextField>
            ) : null}
        </div>
    );
}
